<template>
  <b-card>
    <!-- Table Top -->
    <b-row class="mb-2">

      <!-- Per Page -->
      <b-col
        cols="12"
        md="8"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <b-row>
          <b-col
            md="2"
          >
            <TableEntries
              v-model="perPage"
              :options="perPageOptions"
            />
          </b-col>

          <b-col
            md="4"
          >
            <DatePickerField
              id="start-date"
              v-model="accountCreatedFormData.startDate"
              :min-date="new Date()"
              :label="$t('Start Date')"
              rules="required"
              :error-name="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Start Date')"
              @input="handleDateChange"
            />
          </b-col>

          <b-col
            md="4"
          >
            <DatePickerField
              id="end-date"
              v-model="accountCreatedFormData.endDate"
              rules="required"
              :min-date="new Date()"
              :label="$t('End Date')"
              :error-name="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('End Date')"
              @input="handleDateChange"
            />
          </b-col>

          <!-- Export -->
          <b-col
            md="2"
            class="mt-2"
          >
            <b-dropdown
              v-if="getUserPermission('EXPORT_ACCOUNT_CREATION_REPORT')"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
              :disabled="isDisabled"
            >
              <b-dropdown-item @click="exportToCSV()">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="exportToExcel()">
                {{ $t('Excel') }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
        </b-row>
      </b-col>

      <!-- Search -->
      <b-col
        cols="12"
        md="4"
        class="mt-2"
      >
        <div class="d-flex align-items-center justify-content-lg-end">
          <Button
            :label="$t('Filter')"
            size="md"
            icon="FilterIcon"
            show-icon
            class="mr-1"
            @click="showModal('modal-filter')"
          />
          <Button
            :label="$t('Reset Filter')"
            size="md"
            icon="RotateCwIcon"
            show-icon
            @click="resetFilter"
          />
        </div>
      </b-col>
    </b-row>

    <b-table
      ref="createdAccountTableRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="accountCreatedList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateOfBirth)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(pep)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value === true ? 'Yes' : 'No')"
        >
          {{ data.value === true ? 'Yes' : 'No' }}
        </b-badge>
      </template>
      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(balance)="data">
        {{ data.value ? toBalance(data.value) : '' }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="generateNewlyCreatedAccountReport"
      @cancel="resetFilters"
    >

      <b-row class="match-height">
        <b-col
          md="6"
        >
          <InputField
            id="customer-id"
            v-model="accountCreatedFormData.customerId"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer Id')"
            type="text"
          />
        </b-col>
        <b-col
          md="6"
        >
          <InputField
            id="name"
            v-model="accountCreatedFormData.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter Account Name')"
            type="text"
            :is-allow-special-characters="false"
          />
        </b-col>

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="accountCreatedFormData.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter Account Number')"
            type="number"
            :formatter="checkNumberLimit"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Account Officer -->
        <b-col md="6">
          <SelectField
            id="account-officer"
            v-model="accountCreatedFormData.accountOfficer"
            :label="$t('Account Officer')"
            :placeholder="$t('Select Account Officer')"
            :options="accountOfficerOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Account Officer -->

        <!-- Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="accountCreatedFormData.productCode"
            :label="$t('Product Name')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
            :placeholder="$t('Select product')"
          />
        </b-col>
        <!-- Product -->
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="accountCreatedFormData.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Branch -->
        <!-- Start Date-->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="accountCreatedFormData.startDate"
            :min-date="new Date()"
            :label="$t('Start Date')"
            :error-name="$t('Start Date')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  Start Date -->

        <!-- End Date-->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="accountCreatedFormData.endDate"
            :min-date="new Date()"
            :label="$t('End Date')"
            :error-name="$t('End Date')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  End Date -->
        <b-col md="6">
          <SelectField
            id="created-by"
            v-model="accountCreatedFormData.createdBy"
            :reduce="option => option.value"
            :label="$t('Created By')"
            :placeholder="$t('Select')"
            :options="userOptions"
            item="label"
          />
        </b-col>
        <!-- IPPIS/Oracle ID -->
        <b-col md="6">
          <InputField
            id="ippis-oracle-id"
            v-model="accountCreatedFormData.employeeID"
            :label="$t('IPPIS/Oracle ID')"
            :placeholder="$t('Enter IPPIS/Oracle ID')"
            type="text"
            for="ippis-oracle-id"
          />
        </b-col>
        <!--IPPIS/Oracle ID -->
        <b-col md="6">
          <InputField
            id="phone-number"
            v-model="accountCreatedFormData.phoneNumber"
            :label="$t('Phone Number')"
            :placeholder="$t('Enter phone number')"
            type="number"
            for="phone-number"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="bvn"
            v-model="accountCreatedFormData.bvn"
            :label="$t('BVN')"
            :placeholder="$t('Enter BVN')"
            type="text"
            for="bvn"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="nin"
            v-model="accountCreatedFormData.nin"
            :label="$t('NIN')"
            :placeholder="$t('Enter NIN')"
            type="text"
            for="nin"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="pep"
            v-model="accountCreatedFormData.pep"
            :reduce="option => option.value"
            :label="$t('PEP')"
            :placeholder="$t('Select')"
            :options="translatedPepOptions"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="phone-number"
            v-model="accountCreatedFormData.phoneNumber"
            :label="$t('Phone Number')"
            :placeholder="$t('Enter Phone Number')"
            type="number"
            for="phone-number"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="account-status"
            v-model="accountCreatedFormData.accountStatus"
            :label="$t('Account Status')"
            :placeholder="$t('Select Account Status')"
            :options="translatedCustomerAccountStatusOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BModal, BSpinner, BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
// import { ValidationObserver } from 'vee-validate'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import {
  toYearWithDate, toCustomDate, getDate, toDateWithYear,
} from '@/helpers/formatDate'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import 'jspdf-autotable'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { reportPermissions } from '../../../../helpers/permissions'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import TableEntries from '@/views/components/table/TableEntries.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BModal,
    BTable,
    BBadge,
    BSpinner,
    BDropdown,
    BDropdownItem,
    // BForm,
    Button,
    DatePickerField,
    FullScreenSpinner,
    TablePagination,
    InputField,
    SelectField,
    TableEntries,
    // ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      isBusy: false,
      isDisabledText: false,
      required,
      isLoading: false,
      isDisabled: true,
      showSpinner: false,
      getStatusColor,
      toDateWithYear,
      accountCreatedFormData: {
        startDate: null,
        endDate: null,
        accountNumber: null,
        accountName: null,
        accountOfficer: null,
        productCode: null,
        branchCode: null,
        employeeID: null,
        phoneNumber: null,
        bvn: null,
        nin: null,
        pep: null,
        createdBy: null,
        accountStatus: null,
        customerId: null,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: '',
        defaultDate: '',
      },
      currentPage: 1,
      perPage: 10,
      accountCreatedList: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'categoryOfAccount', sortable: true, label: 'Account Category' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'accountTierLevel', sortable: true, label: 'Tier Level' },
        { key: 'sectorCode', sortable: true, label: 'Sector Code' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'introducerType', sortable: true, label: 'Introducer Type' },
        { key: 'introducerName', sortable: true, label: 'Introducer Name' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'balance', sortable: true, label: 'Balance' },
        { key: 'phoneNumber', sortable: true, label: 'Cust. Phone No' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'dateOfBirth', sortable: true, label: 'Date of Birth' },
        { key: 'createdBy', sortable: true, label: 'Created By' },
        { key: 'tin', sortable: true, label: 'TIN' },
        { key: 'bvn', sortable: true, label: 'BVN' },
        { key: 'nin', sortable: true, label: 'NIN' },
        { key: 'pep', sortable: true, label: 'PEP' },
        { key: 'registrationNumber', sortable: true, label: 'Registration Number' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
      ],
      customerAccountStatusOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Closed', value: 'Closed' },
        { label: 'Dormant', value: 'Dormant' },
        { label: 'Frozen', value: 'Frozen' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'PNC', value: 'PNC' },
        { label: 'PND', value: 'PND' },
      ],
      userOptions: [],
      accountOfficerOptions: [],
      productOptions: [],
      allSearchedData: [],
      pepOptions: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      currentFinancialDate: 'operations/getCurrentFinancialDate',
      institutionDetails: 'auth/getInstitution',
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    getInstitutionFinancialDate() {
      return getDate(this.currentFinancialDate.financialDate)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedCustomerAccountStatusOptions() {
      return this.customerAccountStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedPepOptions() {
      return this.pepOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.generateNewlyCreatedAccountReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.generateNewlyCreatedAccountReport()
    },
  },
  mounted() {
    // this.datePickerConfig.maxDate = this.getInstitutionFinancialDate
    this.generateNewlyCreatedAccountReport()
    this.fetchProducts()
    this.fetchUsers()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getNewlyCreatedAccountReport: 'reports/getNewlyCreatedAccountReport',
      getProducts: 'account/getProducts',
      getUsers: 'admin/getUserList',
      getAccountOfficers: 'account/getAccountOfficerList',
      downloadNewlyCreatedAccounts: 'reports/downloadNewlyCreatedAccounts',
    }),
    checkNumberLimit(e) {
      return String(e).substring(0, 10)
    },
    async fetchUsers() {
      try {
        this.isBusy = true
        const response = await this.getUsers({
          status: 'Active',
          permission: reportPermissions.ACCOUNT_CREATION_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.userOptions = data.data.map(items => ({
            label: `${items.firstname ? items.firstname : ''} ${items.othernames ? items.othernames : ''} ${items.lastname ? items.lastname : ''}`,
            value: items.email,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_CREATION_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchProducts() {
      try {
        const response = await this.getProducts({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_CREATION_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(item => item.type === 'Savings' && item.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async generateNewlyCreatedAccountReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        this.showSpinner = true

        let pepState
        if (this.accountCreatedFormData.pep === 'Yes') {
          pepState = true
        } else if (this.accountCreatedFormData.pep === 'No') {
          pepState = false
        } else {
          pepState = null
        }
        const { data, status } = await this.getNewlyCreatedAccountReport({
          search: {
            ...this.accountCreatedFormData,
            pep: pepState,
            startDate: this.accountCreatedFormData.startDate ? toYearWithDate(this.accountCreatedFormData.startDate) : null,
            endDate: this.accountCreatedFormData.endDate ? toYearWithDate(this.accountCreatedFormData.endDate) : null,
          },
          page: this.currentPage,
          limit: this.perPage,
        })
        if (status === 200 && data.data) {
          this.accountCreatedList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    handleDateChange() {
      if (this.accountCreatedFormData.startDate && this.accountCreatedFormData.endDate) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
      }
    },
    async exportToCSV() {
      try {
        this.isBusy = true
        let pepState
        if (this.accountCreatedFormData.pep === 'Yes') {
          pepState = true
        } else if (this.accountCreatedFormData.pep === 'No') {
          pepState = false
        } else {
          pepState = null
        }
        const response = await this.downloadNewlyCreatedAccounts({
          search: {
            ...this.accountCreatedFormData,
            pep: pepState,
            startDate: this.accountCreatedFormData.startDate ? toYearWithDate(this.accountCreatedFormData.startDate) : null,
            endDate: this.accountCreatedFormData.endDate ? toYearWithDate(this.accountCreatedFormData.endDate) : null,
          },
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_ACCOUNT_CREATION_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async exportToExcel() {
      try {
        this.isBusy = true
        let pepState
        if (this.accountCreatedFormData.pep === 'Yes') {
          pepState = true
        } else if (this.accountCreatedFormData.pep === 'No') {
          pepState = false
        } else {
          pepState = null
        }
        const response = await this.downloadNewlyCreatedAccounts({
          search: {
            ...this.accountCreatedFormData,
            pep: pepState,
            startDate: this.accountCreatedFormData.startDate ? toYearWithDate(this.accountCreatedFormData.startDate) : null,
            endDate: this.accountCreatedFormData.endDate ? toYearWithDate(this.accountCreatedFormData.endDate) : null,
          },
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_ACCOUNT_CREATION_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.accountCreatedFormData = { ...null }
    },

    resetFilter() {
      this.accountCreatedFormData = { ...null }
      this.generateNewlyCreatedAccountReport()
    },
    formatCustomDate: toCustomDate,
    toAmount: formatAmount,
  },
}
</script>

    <style lang="scss">
       @import '@core/scss/vue/libs/vue-select.scss';
    </style>
